import styled from 'styled-components';
import { HeartFilled, HeartOutline, Lotus } from 'src/icons';
import { useCallback } from 'react';
import Tooltip from '@mui/material/Tooltip';

export const Button = styled.button<{ liked?: boolean; pending?: boolean; }>`
  position: absolute;
  z-index: 1;
  display: block;
  width: 32px;
  height: 32px;
  top: 16px;
  right: 16px;
  border-radius: 4px;
  border: none;
  outline: none;
  cursor: pointer;
  background: rgba(100, 100, 100, 0.2);
  backdrop-filter: blur(2px) invert(20%) brightness(120%);

  transition: color .2s;
  color: ${p => p.liked ? p.theme.palette.secondary.main : '#ffffff'};

  ${HeartOutline}, ${HeartFilled} {
    position: absolute;
    top: 4px;
    left: 4px;
    width: 24px;
    height: 24px;
  }

  ${HeartFilled} {
    opacity: ${p => p.liked ? 1 : 0};
    transition: opacity .2s;
  }

  &:hover ${HeartFilled} {
    opacity: ${p => p.liked ? 1 : 0.5};
  }

  &:disabled {
    pointer-events: none;
  }
`;

const LotusIcon = styled.div<{ liked?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: ${p => p.liked ? p.theme.palette.secondary.main : '#ffffff'};

  ${Lotus} {
    width: 100%;
    height: 100%;
  }

  ${Lotus} path {
    fill: ${p => p.liked ? p.theme.palette.secondary.main : 'none'};
    stroke: ${p => p.liked ? 'none' : '#ffffff'};
    stroke-width: ${p => p.liked ? 0 : 16};
    stroke-linejoin: round;
  }
`;

const LotusButton = styled.button<{ liked?: boolean }>`
  position: absolute;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 6px;
  right: 16px;
  bottom: 16px;
  min-width: 56px;
  height: 32px;
  padding: 0 6px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  background: rgba(100, 100, 100, 0.2);
  backdrop-filter: blur(2px) invert(20%) brightness(120%);
  color: #ffffff;

  &:disabled {
    pointer-events: none;
  }
`;

const Counter = styled.span`
  font-size: 13px;
  line-height: 1;
  font-weight: 700;
  color: #ffffff;
`;

const LikeButton: React.FC<{
  liked: boolean;
  onToggle?: () => void;
  showPublicLike?: boolean;
  publicLiked?: boolean;
  publicLikesCount?: number;
  onTogglePublicLike?: () => void;
  publicLikePending?: boolean;
}> = ({
  liked,
  onToggle,
  showPublicLike = false,
  publicLiked = false,
  publicLikesCount = 0,
  onTogglePublicLike,
  publicLikePending = false,
}) => {
  const handleClick = useCallback((event: React.MouseEvent) => {
    if (!onToggle) return;
    event.preventDefault();
    event.stopPropagation();
    onToggle();
  }, [onToggle]);

  const handlePublicClick = useCallback((event: React.MouseEvent) => {
    if (!onTogglePublicLike || publicLikePending) return;
    event.preventDefault();
    event.stopPropagation();
    onTogglePublicLike();
  }, [onTogglePublicLike, publicLikePending]);

  return (
    <>
      <Button
        liked={liked}
        onClick={handleClick}
        disabled={!onToggle}
      >
        <HeartFilled />
        <HeartOutline />
      </Button>
      {showPublicLike && (
        <Tooltip title="Podoba ci się? Daj nam znać!">
          <LotusButton
            liked={publicLiked}
            onClick={handlePublicClick}
            disabled={!onTogglePublicLike || publicLikePending}
          >
            <LotusIcon liked={publicLiked}>
              <Lotus />
            </LotusIcon>
            <Counter>{publicLikesCount}</Counter>
          </LotusButton>
        </Tooltip>
      )}
    </>
  );
}

export default LikeButton;
